import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { PlayIcon, VolumeIcon, CheckCircleIcon, LockIcon, ArrowRightIcon, BookOpenIcon, GraduationCapIcon, ClockIcon, InfoIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const LESSONS = [{
  id: 1,
  title: {
    en: 'Introduction to English Sounds',
    ar: 'مقدمة في أصوات اللغة الإنجليزية'
  },
  description: {
    en: 'Learn the fundamental sounds of English letters and words',
    ar: 'تعلم الأصوات الأساسية للحروف والكلمات الإنجليزية'
  },
  duration: '15 min',
  durationAr: '15 دقيقة',
  isCompleted: false,
  examples: [{
    sound: 'æ',
    words: ['cat', 'hat', 'map'],
    arMeaning: ['قط', 'قبعة', 'خريطة']
  }, {
    sound: 'i:',
    words: ['see', 'tree', 'key'],
    arMeaning: ['يرى', 'شجرة', 'مفتاح']
  }, {
    sound: 'ʌ',
    words: ['cup', 'sun', 'run'],
    arMeaning: ['كوب', 'شمس', 'يجري']
  }]
}, {
  id: 2,
  title: {
    en: 'Basic Greetings',
    ar: 'التحيات الأساسية'
  },
  description: {
    en: 'Learn essential greetings and introductions',
    ar: 'تعلم التحيات والمقدمات الأساسية'
  },
  duration: '20 min',
  durationAr: '20 دقيقة',
  isCompleted: false,
  isLocked: true,
  examples: [{
    phrase: 'Hello',
    ar: 'مرحباً',
    usage: 'General greeting'
  }, {
    phrase: 'Good morning',
    ar: 'صباح الخير',
    usage: 'Morning greeting'
  }, {
    phrase: 'Nice to meet you',
    ar: 'تشرفت بمقابلتك',
    usage: 'First meetings'
  }]
}, {
  id: 3,
  title: {
    en: 'Numbers and Counting',
    ar: 'الأرقام والعد'
  },
  description: {
    en: 'Master numbers and basic counting',
    ar: 'أتقن الأرقام والعد الأساسي'
  },
  duration: '25 min',
  durationAr: '25 دقيقة',
  isCompleted: false,
  isLocked: true,
  examples: [{
    number: '0-10',
    words: ['zero', 'one', 'two'],
    ar: ['صفر', 'واحد', 'اثنان']
  }, {
    number: '11-20',
    words: ['eleven', 'twelve'],
    ar: ['أحد عشر', 'اثنا عشر']
  }, {
    number: 'Ordinal',
    words: ['first', 'second'],
    ar: ['الأول', 'الثاني']
  }]
}];
export function GettingStartedUnit() {
  const {
    isArabic
  } = useLanguage();
  const [activeLesson, setActiveLesson] = useState<number | null>(null);
  return <div className="max-w-4xl mx-auto" data-id="element-492">
      {/* Unit Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-493">
        <div className="flex items-center gap-4 mb-4" data-id="element-494">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-495">
            <GraduationCapIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-496" />
          </div>
          <div data-id="element-497">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-498">
              {isArabic ? 'البداية' : 'Getting Started'}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1" data-id="element-499">
              {isArabic ? 'تعلم الأصوات الإنجليزية الأساسية والتحيات والأرقام' : 'Learn essential English sounds, greetings, and numbers'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-500">
          <div className="flex items-center gap-2" data-id="element-501">
            <ClockIcon className="w-4 h-4" data-id="element-502" />
            <span data-id="element-503">{isArabic ? 'ساعتين' : '2 hours'}</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-504">
            <BookOpenIcon className="w-4 h-4" data-id="element-505" />
            <span data-id="element-506">3 {isArabic ? 'دروس' : 'lessons'}</span>
          </div>
        </div>
      </div>
      {/* Lessons */}
      <div className="space-y-4" data-id="element-507">
        {LESSONS.map((lesson, index) => <div key={lesson.id} className={`bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden
              ${activeLesson === lesson.id ? 'ring-2 ring-blue-500' : ''}`} data-id="element-508">
            <div className="p-6" data-id="element-509">
              <div className="flex items-center justify-between gap-4" data-id="element-510">
                <div className="flex items-center gap-4" data-id="element-511">
                  <div className={`p-3 rounded-lg ${lesson.isCompleted ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`} data-id="element-512">
                    {lesson.isCompleted ? <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-513" /> : lesson.isLocked ? <LockIcon className="w-6 h-6 text-gray-400 dark:text-gray-500" data-id="element-514" /> : <PlayIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-515" />}
                  </div>
                  <div data-id="element-516">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-517">
                      {isArabic ? lesson.title.ar : lesson.title.en}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-518">
                      {isArabic ? lesson.description.ar : lesson.description.en}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-519">
                      <span className="flex items-center gap-1" data-id="element-520">
                        <ClockIcon className="w-4 h-4" data-id="element-521" />
                        {isArabic ? lesson.durationAr : lesson.duration}
                      </span>
                      {lesson.isLocked && <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400" data-id="element-522">
                          <InfoIcon className="w-4 h-4" data-id="element-523" />
                          {isArabic ? 'أكمل الدرس السابق للفتح' : 'Complete previous lesson to unlock'}
                        </span>}
                    </div>
                  </div>
                </div>
                <Link to={`/learn/unit/1/lesson/${lesson.id}`} className={`
                    px-4 py-2 rounded-lg transition-colors whitespace-nowrap
                    ${lesson.isLocked ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' : lesson.isCompleted ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50' : 'bg-blue-600 text-white hover:bg-blue-700'}
                  `} onClick={e => {
              if (lesson.isLocked) e.preventDefault();
            }} data-id="element-524">
                  {lesson.isCompleted ? isArabic ? 'مراجعة' : 'Review' : isArabic ? 'ابدأ الدرس' : 'Start Lesson'}
                </Link>
              </div>
              {/* Example Preview */}
              {activeLesson === lesson.id && <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700" data-id="element-525">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4" data-id="element-526">
                    {isArabic ? 'أمثلة من هذا الدرس' : 'Examples from this lesson'}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-527">
                    {lesson.examples.slice(0, 2).map((example, i) => <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-528">
                        <div data-id="element-529">
                          <span className="text-gray-900 dark:text-white" data-id="element-530">
                            {'sound' in example ? example.sound : 'phrase' in example ? example.phrase : example.number}
                          </span>
                          <span className="block text-sm text-gray-500 dark:text-gray-400" data-id="element-531">
                            {'words' in example ? example.words[0] : 'ar' in example ? example.ar : ''}
                          </span>
                        </div>
                        <button className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-full" onClick={() => {
                  if ('words' in example) {
                    const utterance = new SpeechSynthesisUtterance(example.words[0]);
                    window.speechSynthesis.speak(utterance);
                  }
                }} data-id="element-532">
                          <VolumeIcon className="w-5 h-5" data-id="element-533" />
                        </button>
                      </div>)}
                  </div>
                </div>}
              {/* Preview Toggle */}
              <button onClick={() => setActiveLesson(activeLesson === lesson.id ? null : lesson.id)} className="mt-4 text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1" data-id="element-534">
                {activeLesson === lesson.id ? isArabic ? 'إخفاء الأمثلة' : 'Hide examples' : isArabic ? 'عرض الأمثلة' : 'Show examples'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-535" />
              </button>
            </div>
          </div>)}
      </div>
    </div>;
}